import isEqual from 'lodash/isEqual';
import { FieldOption, StatusGroup } from 'api/tasks';
import { FieldHelper } from 'utils/field';
import { EditorComponents } from '../criteria/editor-components';
import {
  BooleanField,
  DateField,
  FieldType,
  FilterableField,
  MultiLineTextField,
  MultiSelectField,
  MultiUserField,
  NoteField,
  NumberField,
  SingleSelectField,
  SingleUserField,
  TextField,
} from '../fields';
import { FieldFilter } from '../filters';
import {
  BooleanFieldFilter,
  BooleanFilterOperationType,
} from '../filters/boolean';
import { DateFieldFilter, DateFieldFilterOperationType } from '../filters/date';
import { MultiSelectFieldFilter } from '../filters/multi-select';
import { MultiUserFieldFilter } from '../filters/multi-user';
import {
  NumberFieldFilter,
  NumberFilterOperationType,
} from '../filters/number';
import { SelectFilterOperationType } from '../filters/select';
import { SingleSelectFieldFilter } from '../filters/single-select';
import { SingleUserFieldFilter } from '../filters/single-user';
import { TextFieldFilter, TextFieldFilterOperationType } from '../filters/text';
import { TaskCompletionType } from '../views';

export interface GetFilterOperationTypeOptions {
  [FieldType.Date]?: (field: DateField) => DateFieldFilter[];
  [FieldType.Text]?: (
    field: TextField | MultiLineTextField | NoteField,
  ) => TextFieldFilter[];
  [FieldType.Number]?: (field: NumberField) => NumberFieldFilter[];
  [FieldType.Boolean]?: (field: BooleanField) => BooleanFieldFilter[];
  [FieldType.SingleSelect]?: (
    field: SingleUserField | SingleSelectField,
  ) => SingleUserFieldFilter[] | SingleSelectFieldFilter[];
  [FieldType.MultiSelect]?: (
    field: MultiUserField | MultiSelectField,
  ) => MultiUserFieldFilter[] | MultiSelectFieldFilter[];
}

export interface GetOperationOptionRenderer {
  BooleanFilterOperationType?: (
    type: BooleanFilterOperationType,
  ) => null | JSX.Element;
  NumberFilterOperationType?: (
    type: NumberFilterOperationType,
  ) => null | JSX.Element;
  TextFieldFilterOperationType?: (
    type: TextFieldFilterOperationType,
  ) => null | JSX.Element;
  SelectFilterOperationType?: (
    type: SelectFilterOperationType,
  ) => null | JSX.Element;
  DateFieldFilterOperationType?: (
    type: DateFieldFilterOperationType,
    filter?: DateFieldFilter,
  ) => null | JSX.Element;
}

export type GetSelectEditorComponents = (
  field:
    | SingleSelectField
    | MultiSelectField
    | SingleUserField
    | MultiUserField,
) => EditorComponents | undefined;

export const computeShowTaskCriteria = (
  fields_lookup: Record<string, FilterableField>,
  showTask: TaskCompletionType,
): FieldFilter[] => {
  const statusField = fields_lookup.status as SingleSelectField;
  const completed = FieldHelper(statusField)
    .options.activeOptions.filter(
      (x) => (x as FieldOption<StatusGroup>).group === StatusGroup.Completed,
    )
    .map(({ value }) => value);

  switch (showTask) {
    case TaskCompletionType.Incomplete:
      return [
        {
          field: statusField,
          type: SelectFilterOperationType.IsNoneOf,
          value: completed,
        },
      ];
    case TaskCompletionType.Complete:
      return [
        {
          field: statusField,
          type: SelectFilterOperationType.IsOneOf,
          value: completed,
        },
      ];
    case TaskCompletionType.All:
    default:
      return [];
  }
};

export const parseShowTaskCriteria = (
  status: SingleSelectFieldFilter,
  lookup: Record<string, FilterableField>,
): TaskCompletionType | null => {
  const statusField = lookup.status as SingleSelectField;
  const completed = FieldHelper(statusField)
    .options.activeOptions.filter(
      (x) => (x as FieldOption<StatusGroup>).group === StatusGroup.Completed,
    )
    .map(({ value }) => value);

  if (
    status.type === SelectFilterOperationType.IsNoneOf &&
    isEqual(status.value, completed)
  ) {
    return TaskCompletionType.Incomplete;
  }
  if (
    status.type === SelectFilterOperationType.IsOneOf &&
    isEqual(status.value, completed)
  ) {
    return TaskCompletionType.Complete;
  }

  return null;
};
